import { type PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RepubCountry } from '@cx-web/domain-types';
import { featureFlags, useFeatureFlag } from '@cx-web/feature-flags/provider';
import { type RepubIs } from '@cx-web/utils';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { type HistoryData } from '@fpsm/cxy-components/BarChart';
import clsx from 'clsx';

import { type BookmakersConfig } from '@cx-modules/types';

import {
  BarChart,
  BidirectionalBarChart,
  Card,
  GridTable,
  PerformanceIndicator,
  Tabs,
  TabsContent,
  useToggle,
} from '~/apps/product-plus/components/cxy/cxy.ts';
import { type BetInsightsConfig } from '~/apps/product-plus/configuration/BetInsightsConfiguration';
import {
  type BettingInsightsContent,
  type TrendsIndex,
  type ValueBetResult,
} from '~/apps/product-plus/configuration/types';
import { type BettingInsightsUI, type StyleSheet } from '~/apps/product-plus/configuration/ui/BettingInsightsUI';
import { useTranslatedSubeventName } from '~/apps/product-plus/hooks/useTranslatedSubeventName';

import { DefaultTableBody } from '../DefaultTableBody';
import { EventDetails } from '../EventDetails';

import { TrendsStatsTable, type TrendType } from './components/StatsTable';
import styles from './styles/Trends.module.css';

export type Team = {
  badgeUrl: string;
  name: string;
  nickName: string;
  participantId: string;
  shortName: string;
};

const Mavericks: Team = {
  participantId: 'ef55c3ac-f782-4b39-ab21-9c22e1a5571c',
  name: 'Dallas Mavericks',
  shortName: 'DAL',
  nickName: 'Mavericks',
  badgeUrl: 'https://cdn.prod.us.occloud.io/blue/img/team-logos/Q4/ef55c3ac-f782-4b39-ab21-9c22e1a5571c.svg',
};

function TrendsComponent({
  children,
  config,
  content,
  result,
}: {
  config: BetInsightsConfig<TrendsIndex, BettingInsightsContent<TrendsIndex>>;
  content: BettingInsightsContent<TrendsIndex>[];
  result: ValueBetResult;
} & PropsWithChildren) {
  const isCaptionEmpty = !config.ui.card.title.format;
  const subeventLabel = useTranslatedSubeventName(result.categoryGroupName, result.subeventName);

  return (
    <Tabs
      defaultValue={result.sportsData.history && result.sportsData.history.length > 5 ? 'last10' : 'last5'}
      style={{ width: '100%' }}
    >
      <GridTable className={styles.trends} label={subeventLabel} mobileColumns={3}>
        <GridTable.Caption className={{ [styles.hidden]: isCaptionEmpty }}>
          <EventDetails<TrendsIndex> content={content} renderTitle={config.ui.card.title.render} result={result} />
        </GridTable.Caption>
        {children}
      </GridTable>
    </Tabs>
  );
}

function TrendsBody({
  bookmakers,
  content,
  label,
  resultIndex,
  styles,
  table,
}: {
  bookmakers: BookmakersConfig;
  content: ValueBetResult;
  label: string;
  resultIndex: number;
  styles: StyleSheet;
  table: BettingInsightsUI<TrendsIndex, BettingInsightsContent<TrendsIndex>>['card']['table'];
}) {
  return (
    <DefaultTableBody
      bookmakers={bookmakers}
      content={content}
      label={label}
      resultIndex={resultIndex}
      styles={styles}
      // @ts-expect-error - TypeScript structural typing false positive: factory-created table type is structurally identical but seen as unrelated due to module boundary inference. See docs/refactoring-config-to-functional.md for details.
      table={table}
    />
  );
}

function TrendsFooter({
  content,
  formatDate,
  repubCountry,
  repubIs,
  resourcesUrl,
}: {
  content: ValueBetResult;
  formatDate: (date: string, format?: string) => string;
  repubCountry: RepubCountry;
  repubIs: RepubIs;
  resourcesUrl: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMoreStats = () => {
    if (!isOpen) {
      trackEvent('trends_stats_expand', {
        eventLabel: 'Trends Stats Expanded',
      });
    }

    setIsOpen(!isOpen);
  };

  const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({
    isOpen,
    onChange: toggleMoreStats,
  });

  const isBarChartEnabled = useFeatureFlag(featureFlags.SUBS__PLUS__TRENDS__BARCHART);
  const isStatsTableEnabled = useFeatureFlag(featureFlags.SUBS__PLUS__STATS_TABLE);

  // 2147 is handicap but we can handle this more gracefully after xmas
  const showBidirectionalBarChart = content.betTypeId === 1 || content.betTypeId === 2147;

  const { t } = useTranslation();

  const barChartTranslatedLabels = {
    All: t('CX_PLUS_BARCHART_ALL'),
    Home: t('CX_PLUS_BARCHART_HOME'),
    Away: t('CX_PLUS_BARCHART_AWAY'),
    versus: t('CX_PLUS_BARCHART_VERSUS'),
  };

  return (
    <GridTable.Footer className={styles.footer}>
      <GridTable.Row className={styles.footerRow}>
        <GridTable.Cell className={styles.footerCell} style={{ gridColumn: '1 / -1' }}>
          {!!content.sportsData?.trends &&
            Object.entries(content.sportsData?.trends).map(([trend, trendData]) => {
              const length = trend.replace('last', '');

              return (
                <TabsContent
                  className={styles.tabContent}
                  key={`${trend}-content-${content.betId}`}
                  style={{ width: '100%' }}
                  value={trend}
                >
                  <>
                    {trend !== length && content.sportsData?.history?.slice(0, Number(length)) && (
                      <>
                        <div className={styles.hitRate}>
                          <p>{trendData.trendMap?.[repubCountry] || trendData.trend}</p>
                          <PerformanceIndicator
                            americanFormat={repubIs.US}
                            hasMaxWidth={false}
                            // todo: clean up this type so casting isn't necessary
                            history={content.sportsData.history as HistoryData[]}
                            hitRate={trendData.hitRate ?? undefined}
                            limit={Number(length)}
                            showMatchInformation
                            spacing="s"
                          />
                        </div>
                        <div
                          className={clsx(styles.statsTrigger, triggerClass)}
                          {...headingProps}
                          aria-expanded={headingProps['aria-expanded'] as boolean | undefined}
                          data-disabled={!(content.sportsData?.history?.length > 0)}
                        >
                          <img
                            alt="Stats Icon"
                            className={styles.statsIcon}
                            src={`${resourcesUrl}/assets/img/plus/stats.svg`}
                          />
                          <span>
                            {isOpen ? t('CX_PLUS_TRENDS_SEE_LESS') : t('CX_PLUS_TRENDS_SEE_MORE')}{' '}
                            {t('CX_PLUS_TRENDS_ANALYSIS')}
                          </span>
                          <Indicator />
                        </div>

                        <div {...sectionProps} className={styles.trendContainer}>
                          {isOpen ? (
                            <>
                              {isBarChartEnabled && (
                                <div
                                  className={styles.barChartWrapper}
                                  data-testid={showBidirectionalBarChart ? 'bi-directional-bar-chart' : 'bar-chart'}
                                >
                                  {showBidirectionalBarChart ? (
                                    <BidirectionalBarChart
                                      americanFormat={repubIs.US}
                                      barRadius={repubIs.GZ ? 2 : 0}
                                      customLabels={barChartTranslatedLabels}
                                      historyData={content.sportsData.history as HistoryData[]}
                                      limit={Number(length)}
                                      oppositionTeam={Mavericks}
                                    />
                                  ) : (
                                    <BarChart
                                      alternativeLines={t('CX_PLUS_TRENDS_ALTERNATIVE_LINES')}
                                      americanFormat={repubIs.US}
                                      barRadius={repubIs.GZ ? 2 : 0}
                                      customLabels={barChartTranslatedLabels}
                                      flipBarSuccess={content.line?.sign === '-' || content.line?.sign === 'Under'}
                                      historyData={content.sportsData.history as HistoryData[]}
                                      limit={Number(length)}
                                      line={Number(content.line?.absolute)}
                                      oppositionTeam={Mavericks}
                                    />
                                  )}
                                </div>
                              )}
                              {content.sportsData.statsTable && isStatsTableEnabled && (
                                <TrendsStatsTable
                                  content={content.sportsData.statsTable}
                                  formatDate={formatDate}
                                  length={Number(length)}
                                  trend={trend as TrendType}
                                />
                              )}
                              {!isBarChartEnabled && !isStatsTableEnabled && content.sportsData.statsTable && (
                                <Card height="80px" padding="l" shouldCenter variant="secondary">
                                  {t('CX_PLUS_COMING_SOON')}
                                </Card>
                              )}
                            </>
                          ) : null}
                        </div>
                      </>
                    )}
                  </>
                </TabsContent>
              );
            })}
        </GridTable.Cell>
      </GridTable.Row>
    </GridTable.Footer>
  );
}

TrendsComponent.Body = TrendsBody;
TrendsComponent.Footer = TrendsFooter;

export { TrendsComponent as Trends };
